/**
 * Login.tsx
 *
 * Login page — handles email/password login and MFA two-factor flow.
 *
 * @version 1.0.0
 * @author Frontend Team
 * @date 2026-04-07
 * @modified 2026-04-07
 * @copyright © 2026 Thesis Pte Ltd. All rights reserved.
 */
import React, { useState } from "react"
import PageLayoutAuth from "./PageLayoutAuth"
import InputDefault from "components/Input/InputDefault"
import FormInputCard from "./molecules/FormInputCard"
import Header from "./atoms/Header"
import LabelChangeRouter from "./molecules/LabelChangeRouter"
import RememberMeCard from "./molecules/RememberMeCard"
import { PATH } from "constants/path"
import { LoginRequest, MFAMethod, defaultLoginRequest } from "./types"
import { STATUS_INPUT } from "components/Input/types"
import {
  isValidEmail,
  changeLabelStatusEmail,
  umamiTracking,
} from "helpers/utils"
import { cloneDeep, isNull, isUndefined } from "lodash"
import { useArray, useBoolean, useNumber } from "helpers/hooks"
import { loginMiddleware } from "./services/api"
import { pushTo } from "helpers/history"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { toast } from "react-toastify"
import Button from "components/Button/Button"
import { ACTION_RECAPTCHA } from "constants/actionReCaptcha"
import { useRecaptchaToken } from "hooks/useRecaptchaToken"
import { EVENT } from "constants/events"
import { TwoFactorForm } from "./molecules/TwoFactorForm"
import * as Sentry from "@sentry/react"

interface LoginFormProps {
  onLogin: (data: LoginRequest) => void
}

enum LoginFormType {
  Login,
  TwoFactor,
}
const LoginForm = (props: LoginFormProps) => {
  const { onLogin } = props
  const [request, setRequest] = useState<LoginRequest>(defaultLoginRequest)
  const [statusInputEmail, setStatusInputEmail] = useState({
    status: STATUS_INPUT.DEFAULT,
    label: "",
  })
  const checkbox = useBoolean()
  const { getToken } = useRecaptchaToken()

  const onSubmit = async () => {
    try {
      const token = await getToken(ACTION_RECAPTCHA.LOGIN)
      onLogin({
        ...request,
        captcha: token,
      })
      umamiTracking(EVENT.LOGIN)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const handleChangeInput =
    (key: "email" | "password") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRequest({
        ...request,
        [key]: event.target.value,
      })
      if (key === "email") {
        setStatusInputEmail(changeLabelStatusEmail(event.target.value))
      }
    }
  const onKeyPress = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (handleDisableButton()) {
        return
      }
      onSubmit()
    }
  }

  const handleDisableButton = () => {
    const newRequest = cloneDeep(request)
    let isDisable = false
    Object.values(newRequest).forEach((el, index) => {
      if (!el) {
        isDisable = true
      } else {
        switch (Object.keys(newRequest)[index]) {
          case "email":
            if (!isValidEmail(el)) {
              isDisable = true
            }
            break
          case "password":
            isDisable = el.length < 1
            break
        }
      }
    })

    return isDisable
  }

  return (
    <div>
      <Header title="Log In to Tracelium" />
      <LabelChangeRouter
        title="Don’t have an account?"
        label="Sign Up"
        path={PATH.signUp}
      />
      <div className="mt-6">
        <FormInputCard title="Email">
          <InputDefault
            type="email"
            value={request.email}
            onChange={handleChangeInput("email")}
            status={statusInputEmail.status}
            labelStatus={statusInputEmail.label}
            onKeyPress={onKeyPress}
          />
        </FormInputCard>
        <FormInputCard title="Password">
          <InputDefault
            type="password"
            isPassword
            value={request.password}
            onChange={handleChangeInput("password")}
            onKeyPress={onKeyPress}
          />
        </FormInputCard>
      </div>
      <div className="mt-3 mb-6 flex items-center justify-between">
        <RememberMeCard
          title="Remember me"
          checked={checkbox.value}
          setChecked={checkbox.setValue}
        />
        <p
          className="font-normal cursor-pointer custom-text-black"
          style={{
            fontSize: 14,
            lineHeight: "24px",
            color: "#7A7A7A",
          }}
          onClick={() => pushTo(PATH.forget)}
        >
          Forgot Password?
        </p>
      </div>
      <Button
        title="Log in"
        onClick={onSubmit}
        isDisabledBtn
        widthBtn="100%"
        disabled={handleDisableButton()}
      />
    </div>
  )
}

const Login = () => {
  const [formType, setFormType] = useState<LoginFormType>(LoginFormType.Login)
  const [oneTimeToken, setOneTimeToken] = useState<string>("")
  const mfaMethods = useArray([])
  const [labelMfa, setLabelMfa] = useState<{
    maskEmail: string
    maskPhone: string
  }>({
    maskEmail: "",
    maskPhone: "",
  })
  const originMFAMethod = useNumber(MFAMethod.authenticatorApp)

  const onLogin = async (loginData: LoginRequest) => {
    loginMiddleware(loginData)
      .then(async (response) => {
        setOneTimeToken(response.token)
        setFormType(LoginFormType.TwoFactor)
        setLabelMfa({
          maskEmail: response.maskEmail,
          maskPhone: response.maskPhone,
        })
        if (
          !isUndefined(response.methodDefault) &&
          !isNull(response.methodDefault)
        ) {
          originMFAMethod.setValue(response.methodDefault)
        }
        mfaMethods.setValue(response.mfaMethods)
      })
      .catch((error) => {
        const message =
          error.response?.data?.message ??
          "Please enter a correct email and password. Note that both fields may be case-sensitive!"
        toast(<LabelNotificationPage messenger={message} type="error" />)
      })
  }
  return (
    <PageLayoutAuth showPrivacy>
      <div className="h-full w-full flex flex-col p-6 relative">
        {formType === LoginFormType.Login ? (
          <LoginForm onLogin={onLogin} />
        ) : (
          <TwoFactorForm
            oneTimeToken={oneTimeToken}
            setOneTimeToken={setOneTimeToken}
            mfaMethods={mfaMethods.value}
            labelMfa={labelMfa}
            originMFAMethod={originMFAMethod.value}
          />
        )}
      </div>
    </PageLayoutAuth>
  )
}
export default Login
